package com.bikompanion.data.rating

import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.FirebaseApp
import dev.gitlive.firebase.auth.FirebaseAuth
import dev.gitlive.firebase.auth.auth
import dev.gitlive.firebase.database.FirebaseDatabase
import dev.gitlive.firebase.database.database
import dev.zacsweers.metro.BindingContainer
import dev.zacsweers.metro.ContributesTo
import dev.zacsweers.metro.Provides
import dev.zacsweers.metro.SingleIn
import com.bikompanion.data.BuildKonfig
import com.bikompanion.data.rating.firebase.initializeFirebase
import com.bikompanion.domain.di.AppScope

/** Where the Auth and Realtime Database emulators listen, when [BuildKonfig.FIREBASE_EMULATOR_HOST] names a host. */
private const val AuthEmulatorPort = 9099
private const val DatabaseEmulatorPort = 9000

/**
 * The single place that decides where bike votes are stored.
 *
 * The first thing in this project to need scoping. Firebase must be brought up exactly once per
 * process — a second `initialize` on the browser throws, and on the desktop it would install a
 * second platform under the running one — so [provideFirebaseApp] being a singleton is what makes
 * the rest of this correct, not a performance nicety. The same goes for the two services below:
 * `useEmulator` has to run before anything else touches them, and throws if it doesn't.
 *
 * Only bound on Android and the web in practice: the desktop app's graph exposes no rating entry
 * point, so nothing here is ever instantiated there. It still has to compile, which is why
 * `:shared:data` carries the JVM Firebase implementation at all.
 */
@ContributesTo(AppScope::class)
@BindingContainer
object RatingProviders {
    @Provides
    @SingleIn(AppScope::class)
    fun provideFirebaseApp(): FirebaseApp = initializeFirebase()

    @Provides
    @SingleIn(AppScope::class)
    fun provideFirebaseAuth(app: FirebaseApp): FirebaseAuth =
        Firebase.auth(app).also { auth ->
            emulatorHost()?.let { auth.useEmulator(it, AuthEmulatorPort) }
        }

    @Provides
    @SingleIn(AppScope::class)
    fun provideFirebaseDatabase(app: FirebaseApp): FirebaseDatabase =
        Firebase.database(app, BuildKonfig.FIREBASE_DB_URL).also { database ->
            emulatorHost()?.let { database.useEmulator(it, DatabaseEmulatorPort) }
        }
}

/** The emulator to talk to instead of the real project, or null to use the real one. */
private fun emulatorHost(): String? = BuildKonfig.FIREBASE_EMULATOR_HOST.takeIf { it.isNotEmpty() }
